import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { X } from 'lucide-react';
import { trackCTAClick, trackNavigation } from '../utils/analytics';

const testimonials = [
  { quote: "I love how good your chatbot is and how there is a career progression view. Pretty great features", author: "Sree", role: "GenAI Consultant" },
  { quote: "Fig is the best! It feels like you're talking to a person and it's so easy to use!", author: "Alexa", role: "Financial Analyst" },
  { quote: "It allows me to explore different career options that I'd never considered before! Total game changer!", author: "Stanley", role: "AI Fellow" },
  { quote: "Loved how the career roadmap builds one after another, with day-to-day details and portfolio examples.", author: "Geeta", role: "Product Analyst" },
  { quote: "It's so great — I love how it analysed my interests and I highly recommend it.", author: "Mariam", role: "Software Engineer" },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, loading, hasDoneOnboarding } = useAuth();
  const [showCreatorsNote, setShowCreatorsNote] = useState(false);
  const [visibleBubbles, setVisibleBubbles] = useState([false, false, false, false, false]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const panelRef = useRef(null);
  const cursorRef = useRef(null);
  const bubbleRefs = useRef([]);
  const hasAnimatedRef = useRef(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();

        // no-op — cursor runs on all screen sizes

        // guard against StrictMode double-invoke or any other re-fire when bubbles are already shown
        if (hasAnimatedRef.current) return;
        hasAnimatedRef.current = true;

        const panel = panelRef.current;
        const cursor = cursorRef.current;
        if (!panel || !cursor) return;

        const TRAVEL = 480;
        const CLICK_DUR = 150;
        const PAUSE = 120;

        // place cursor at panel center, invisible
        cursor.style.transition = 'none';
        cursor.style.opacity = '0';
        const panelRect = panel.getBoundingClientRect();
        cursor.style.left = '50%';
        cursor.style.top = '50%';

        const visitBubble = (idx, startDelay) => {
          setTimeout(() => {
            const bubbleEl = bubbleRefs.current[idx];
            if (!bubbleEl) return;
            const pr = panel.getBoundingClientRect();
            const br = bubbleEl.getBoundingClientRect();
            const cx = ((br.left + br.width / 2) - pr.left) / pr.width * 100;
            const cy = ((br.top  + br.height / 2) - pr.top) / pr.height * 100;

            // fade in then travel
            cursor.style.opacity = '1';
            cursor.style.transition = `left ${TRAVEL}ms cubic-bezier(0.4,0,0.2,1), top ${TRAVEL}ms cubic-bezier(0.4,0,0.2,1)`;
            cursor.style.left = `${cx}%`;
            cursor.style.top  = `${cy}%`;

            // click on arrival
            setTimeout(() => {
              cursor.classList.add('cursor-clicking');
              setTimeout(() => {
                cursor.classList.remove('cursor-clicking');
                setVisibleBubbles(prev => {
                  const next = [...prev];
                  next[idx] = true;
                  return next;
                });
              }, CLICK_DUR);
            }, TRAVEL);
          }, startDelay);
        };

        const step = TRAVEL + CLICK_DUR + PAUSE;
        [0, 1, 2, 3, 4].forEach((idx, i) => visitBubble(idx, 400 + i * step));

        // hide cursor after last bubble
        setTimeout(() => { cursor.style.opacity = '0'; }, 400 + 5 * step);
      },
      { threshold: 0.25 }
    );
    if (panelRef.current) observer.observe(panelRef.current);
    return () => observer.disconnect();
  }, []);


  if (!loading && user) {
    return hasDoneOnboarding
      ? <Navigate to="/roadmap" replace />
      : <Navigate to="/data-privacy" replace />;
  }

  const handleGetStarted = () => {
    trackCTAClick('Get Started', 'landing_page');
    if (user) {
      if (hasDoneOnboarding) {
        trackNavigation('landing_page', 'roadmap');
        navigate('/roadmap');
      } else {
        trackNavigation('landing_page', 'data-privacy');
        navigate('/data-privacy');
      }
    } else {
      trackNavigation('landing_page', 'connect_email');
      navigate('/connect-email');
    }
  };

  return (
    <div style={{ fontFamily: "'Montserrat', sans-serif", background: '#000000' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,400;0,500;0,600;0,700;0,800;0,900;1,400;1,500&family=Cinzel:wght@400;600;700&family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Abril+Fatface&family=Inter:wght@400;500;600;700&family=Merriweather:wght@400;700&family=Chango&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }

        .fig-nav {
          position: fixed;
          top: 0; left: 0; right: 0;
          z-index: 100;
          height: 87px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 7%;
          /* Liquid glass */
          background: linear-gradient(
            135deg,
            rgba(255,255,255,0.22) 0%,
            rgba(255,255,255,0.10) 50%,
            rgba(255,255,255,0.16) 100%
          );
          backdrop-filter: blur(48px) saturate(200%) brightness(1.08);
          -webkit-backdrop-filter: blur(48px) saturate(200%) brightness(1.08);
          border-bottom: 1px solid rgba(255,255,255,0.35);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.55),
            inset 0 -1px 0 rgba(255,255,255,0.12),
            0 4px 24px rgba(0,0,0,0.08);
        }

        .fig-nav-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .fig-nav-logo-text {
          font-family: 'Chango', cursive;
          font-weight: 400;
          font-size: 51px;
          letter-spacing: 0.07em;
          color: #ffffff;
          text-shadow: 0 1px 8px rgba(0,0,0,0.25);
          line-height: 1;
        }

        .fig-nav-tagline {
          font-family: 'Montserrat', sans-serif;
          font-size: 16px;
          font-weight: 700;
          letter-spacing: 0.13em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.85);
          line-height: 1.6;
          text-shadow: 0 1px 6px rgba(0,0,0,0.2);
        }

        .fig-nav-right {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .nav-link {
          font-family: 'Montserrat', sans-serif;
          font-size: 16px;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.7);
          background: none;
          border: none;
          cursor: pointer;
          transition: color 0.2s;
          text-shadow: 0 1px 6px rgba(0,0,0,0.2);
        }
        .nav-link:hover { color: #fff; }

        .nav-download-btn {
          font-family: 'Montserrat', sans-serif;
          font-size: 18px;
          font-weight: 700;
          letter-spacing: 0.04em;
          background: rgba(255,255,255,0.92);
          color: #1a1a1a;
          border: 1px solid rgba(255,255,255,0.6);
          border-radius: 100px;
          padding: 12px 32px;
          cursor: pointer;
          transition: transform 0.15s, box-shadow 0.15s, background 0.15s;
          box-shadow: 0 2px 12px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.9);
        }
        .nav-download-btn:hover {
          transform: scale(1.04);
          background: #ffffff;
          box-shadow: 0 4px 20px rgba(0,0,0,0.25);
        }

        /* ── Hero Section ── */
        .hero-section {
          position: relative;
          z-index: 1;
          width: 100%;
          height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background-image: url(/bluewhite.png);
          background-size: cover;
          background-position: center;
          overflow: hidden;
        }

        /* subtle dark gradient at very top so nav is readable */
        .hero-section::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 180px;
          background: linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, transparent 100%);
          pointer-events: none;
          z-index: 2;
        }

        /* Soft bottom fade so hero sky bleeds into the black below */
        .hero-section::after {
          content: '';
          position: absolute;
          bottom: 0; left: 0; right: 0;
          height: 28%;
          background: linear-gradient(to bottom, transparent 0%, #000000 100%);
          pointer-events: none;
          z-index: 2;
        }

        .hero-content {
          position: relative;
          z-index: 20;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 0;
          padding: 0 24px;
        }

        /* ── Computer Panel ── */
        .computer-panel {
          position: relative;
          z-index: 2;
          width: 100%;
          background: linear-gradient(to bottom, transparent 0%, transparent 72%, #000000 98%);
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 0 7% 80px;
          margin-top: -20vh;
        }

        .computer-video-wrap {
          width: 100%;
          max-width: 1100px;
          display: block;
        }
        .computer-video-wrap video {
          width: 100%;
          display: block;
          mix-blend-mode: screen;
        }

        @media (max-width: 768px) {
          .computer-panel { padding: 0 4% 56px; margin-top: -12vh; }
        }

        @keyframes fig-bob {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }

        .hero-wordmark {
          font-family: 'Chango', cursive;
          font-weight: 400;
          font-size: clamp(72px, 14vw, 160px);
          letter-spacing: 0.08em;
          color: #ffffff;
          line-height: 0.9;
          text-shadow: 0 4px 40px rgba(0,0,0,0.25);
          margin-bottom: 8px;
          display: flex;
          justify-content: center;
        }

        .hero-wordmark span {
          display: inline-block;
          animation: fig-bob 3s ease-in-out infinite;
        }
        .hero-wordmark span:nth-child(1) { animation-delay: 0s; }
        .hero-wordmark span:nth-child(2) { animation-delay: 0.3s; }
        .hero-wordmark span:nth-child(3) { animation-delay: 0.6s; }

        .hero-tagline {
          font-family: 'Montserrat', sans-serif;
          font-weight: 800;
          font-size: clamp(18px, 4vw, 52px);
          letter-spacing: 0.04em;
          color: #ffffff;
          line-height: 1.1;
          text-shadow: 0 2px 20px rgba(0,0,0,0.2);
          margin-bottom: 16px;
        }

        .hero-subtitle {
          font-family: 'Montserrat', sans-serif;
          font-weight: 500;
          font-size: clamp(17px, 2vw, 22px);
          color: rgba(230,230,230,0.85);
          line-height: 1.6;
          max-width: 480px;
          text-align: center;
          margin-bottom: 40px;
          text-shadow: 0 1px 8px rgba(0,0,0,0.3);
        }

        .hero-cta {
          font-family: 'Montserrat', sans-serif;
          font-weight: 700;
          font-size: 20px;
          letter-spacing: 0.02em;
          background: #000000;
          color: #ffffff;
          border: none;
          border-radius: 100px;
          padding: 18px 52px;
          cursor: pointer;
          transition: transform 0.2s, box-shadow 0.2s;
          box-shadow: 0 6px 28px rgba(0,0,0,0.35);
        }
        .hero-cta:hover {
          transform: scale(1.05);
          box-shadow: 0 10px 36px rgba(0,0,0,0.45);
        }

        /* ── Social Proof Section ── */
        .social-section {
          width: 100%;
          background: #000000;
          padding: 36px 7% 64px;
        }

        .social-panel {
          position: relative;
          width: 100%;
          min-height: 100vh;
          border-radius: 36px;
          overflow: hidden;
          background-image: url(/bluewhite.png);
          background-size: cover;
          background-position: center;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .social-panel::before {
          content: '';
          position: absolute;
          inset: 0;
          background:
            linear-gradient(to right,  rgba(0,0,0,0.32) 0%, transparent 35%),
            linear-gradient(to left,   rgba(0,0,0,0.32) 0%, transparent 35%),
            linear-gradient(to bottom, rgba(0,0,0,0.4)  0%, transparent 35%),
            linear-gradient(to top,    rgba(0,0,0,0.4)  0%, transparent 35%);
          pointer-events: none;
          z-index: 1;
        }

        .social-content {
          position: relative;
          z-index: 2;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 0 24px;
          width: min(68vw, 960px);
        }

        .social-eyebrow {
          font-family: 'Montserrat', sans-serif;
          font-size: clamp(16px, 2vw, 22px);
          font-weight: 600;
          letter-spacing: 0.08em;
          color: rgba(255,255,255,0.85);
          margin-bottom: 18px;
        }

        .social-headline {
          font-family: 'Playfair Display', Georgia, serif;
          font-weight: 700;
          font-size: clamp(24px, 3.2vw, 44px);
          color: #ffffff;
          line-height: 1.15;
          text-shadow: 0 2px 24px rgba(0,0,0,0.35);
          margin-bottom: 36px;
          width: 100%;
          white-space: pre-line;
          overflow-wrap: normal;
          word-break: keep-all;
        }

        .social-cta {
          font-family: 'Montserrat', sans-serif;
          font-weight: 700;
          font-size: 15px;
          letter-spacing: 0.02em;
          background: #000000;
          color: #ffffff;
          border: none;
          border-radius: 100px;
          padding: 14px 40px;
          cursor: pointer;
          transition: transform 0.2s, box-shadow 0.2s;
          box-shadow: 0 4px 20px rgba(0,0,0,0.5);
        }
        .social-cta:hover {
          transform: scale(1.05);
          box-shadow: 0 8px 28px rgba(0,0,0,0.6);
        }

        /* animated fake cursor */
        .fake-cursor {
          position: absolute;
          z-index: 30;
          pointer-events: none;
          opacity: 0;
          transform: translate(-50%, -50%);
          transition: opacity 0.3s;
          filter: drop-shadow(0 2px 6px rgba(0,0,0,0.4));
        }
        @keyframes cursorClick {
          0%   { transform: translate(-50%, -50%) scale(1); }
          40%  { transform: translate(-50%, -50%) scale(0.65); }
          100% { transform: translate(-50%, -50%) scale(1); }
        }
        .fake-cursor.cursor-clicking {
          animation: cursorClick 0.26s ease-in-out forwards;
        }

        @keyframes bubblePop {
          0%   { opacity: 0; transform: scale(0.5) translateY(28px); }
          65%  { opacity: 1; transform: scale(1.07) translateY(-6px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }

        /* iMessage-style bubbles */
        .t-bubble {
          position: absolute;
          z-index: 3;
          background: rgba(255,255,255,0.92);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          padding: 16px 20px;
          width: 320px;
          box-shadow: 0 2px 16px rgba(0,0,0,0.18);
          opacity: 0;
        }

        .t-bubble.pop-in {
          animation: bubblePop 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }

        .t-bubble-left {
          border-radius: 18px 18px 18px 4px;
        }
        .t-bubble-left::after {
          content: '';
          position: absolute;
          bottom: -9px;
          left: 16px;
          border-top: 10px solid rgba(255,255,255,0.92);
          border-right: 10px solid transparent;
        }

        .t-bubble-right {
          border-radius: 18px 18px 4px 18px;
        }
        .t-bubble-right::after {
          content: '';
          position: absolute;
          bottom: -9px;
          right: 16px;
          border-top: 10px solid rgba(255,255,255,0.92);
          border-left: 10px solid transparent;
        }

        .t-bubble-quote {
          font-family: 'Montserrat', sans-serif;
          font-size: 15px;
          font-weight: 500;
          color: #1a1a1a;
          line-height: 1.6;
          margin: 0 0 10px 0;
        }

        .t-bubble-author {
          font-family: 'Montserrat', sans-serif;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: #777;
          margin: 0;
        }

        /* mobile testimonial strip — horizontal scroll */
        .mobile-testimonials {
          display: none;
        }
        @media (max-width: 768px) {
          /* hero */
          .hero-tagline { font-size: clamp(18px, 5vw, 28px); }
          .hero-subtitle { font-size: 15px; max-width: 300px; }
          .hero-cta { padding: 14px 36px; font-size: 16px; }

          /* social panel — fixed height so % positions work for bubbles */
          .social-panel { min-height: 100vh; height: auto; padding: 0; border-radius: 28px; }
          .social-content { width: 52%; padding: 0 8px; }
          .social-headline { white-space: normal; font-size: clamp(16px, 4.5vw, 24px); }
          .social-eyebrow { font-size: 12px; margin-bottom: 8px; }
          .social-cta { padding: 10px 24px; font-size: 12px; }

          /* shrink bubbles to fit mobile — narrower so they don't overlap center */
          .t-bubble { width: 36vw; padding: 9px 11px; max-width: 140px; }
          .t-bubble-left { border-radius: 14px 14px 14px 3px; }
          .t-bubble-right { border-radius: 14px 14px 3px 14px; }
          .t-bubble-quote { font-size: 10px; line-height: 1.4; margin-bottom: 4px; }
          .t-bubble-author { font-size: 8.5px; }

          /* mobile positions — bring top bubbles closer to center, spread bottom ones */
          .t-b0 { top: 16% !important; left: 4% !important; }
          .t-b1 { top: 16% !important; right: 4% !important; }
          .t-b2 { top: 54% !important; left: 2% !important; }
          .t-b3 { top: 72% !important; right: 2% !important; }
          .t-b4 { top: 86% !important; left: 18% !important; }

          /* hide the horizontal strip since bubbles are now showing */
          .mobile-testimonials { display: none !important; }

          /* video — remove blend mode so it's always visible on mobile */
          .computer-video-wrap video { mix-blend-mode: normal; }
          .computer-panel { background: linear-gradient(to bottom, #1a1a2e 0%, #000 100%); }

          /* footer */
          .fig-footer { padding: 40px 4% 28px; }
          .footer-wordmark { font-size: clamp(40px, 12vw, 72px); }
          .footer-bottom { flex-direction: column; align-items: flex-start; gap: 8px; }
        }

        /* ── Footer ── */
        .fig-footer {
          background: #000000;
          padding: 48px 7% 32px;
          border-top: 1px solid rgba(255,255,255,0.06);
        }

        .footer-inner {
          display: flex;
          flex-direction: column;
          gap: 32px;
        }

        .footer-wordmark {
          font-family: 'Chango', cursive;
          font-weight: 400;
          font-size: clamp(52px, 8vw, 96px);
          letter-spacing: 0.06em;
          color: #ffffff;
          line-height: 1;
        }

        .footer-bottom {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          padding-top: 16px;
          border-top: 1px solid rgba(255,255,255,0.06);
        }

        .footer-copy {
          font-family: 'Montserrat', sans-serif;
          font-size: 18px;
          font-weight: 500;
          color: rgba(255,255,255,0.25);
        }

        .footer-links {
          display: flex;
          gap: 20px;
        }

        .footer-link {
          font-family: 'Montserrat', sans-serif;
          font-size: 12px;
          font-weight: 600;
          color: rgba(255,255,255,0.35);
          text-decoration: none;
          transition: color 0.2s;
        }
        .footer-link:hover { color: rgba(255,255,255,0.8); }

        .footer-social {
          display: flex;
          gap: 18px;
          align-items: center;
          margin-top: 16px;
        }

        .footer-social-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(255,255,255,0.35);
          transition: color 0.2s;
          text-decoration: none;
        }
        .footer-social-icon:hover { color: rgba(255,255,255,0.85); }

        /* Modal */
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.96) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        .modal-card { animation: modalIn 0.25s ease-out forwards; }

        @media (max-width: 600px) {
          /* Nav */
          .fig-nav { padding: 0 4%; height: 60px; }
          .fig-nav-logo-text { font-size: 24px; }
          .fig-nav-tagline { display: none; }
          .nav-link { display: none; }
          .nav-download-btn { font-size: 13px; padding: 8px 18px; }

          /* Hero */
          .hero-wordmark { font-size: clamp(64px, 18vw, 100px); }
          .hero-tagline { font-size: clamp(15px, 4.5vw, 22px); }
          .hero-subtitle { font-size: 13px; max-width: 240px; margin-bottom: 24px; }
          .hero-cta { font-size: 15px; padding: 13px 32px; }
          .hero-section::before { height: 80px; }
          .hero-section::after { height: 15%; }

          /* Computer panel */
          .computer-panel { margin-top: -6vh; padding: 0 0 32px; }

          /* Press bar — stack vertically */
          .press-logos { flex-direction: column; gap: 16px; align-items: center; }
          .press-divider { display: none; }

          /* Social panel */
          .social-panel { border-radius: 20px; padding: 48px 0 40px; }
          .social-content { width: 90%; }
          .social-headline { font-size: clamp(18px, 5.5vw, 26px); word-break: normal; }
          .social-eyebrow { font-size: 12px; margin-bottom: 10px; }
          .social-cta { font-size: 14px; padding: 12px 28px; }

          /* Footer */
          .fig-footer { padding: 32px 4% 20px; }
          .footer-wordmark { font-size: clamp(36px, 15vw, 60px); }
          .footer-inner { gap: 20px; }
          .footer-bottom { flex-direction: column; align-items: flex-start; gap: 8px; }
          .footer-links { flex-direction: column; gap: 10px; }
          .footer-copy { font-size: 14px; }
          .footer-link { font-size: 13px; }

          /* Modal */
          .modal-card { padding: 32px 24px !important; border-radius: 24px !important; }
        }
      `}</style>

      {/* Nav */}
      <nav className="fig-nav">
        <div className="fig-nav-left">
          <img src="/figlogo.png" alt="Fig" style={{ width: 45, height: 45, objectFit: 'contain', borderRadius: 10 }} />
          <span className="fig-nav-logo-text">FIG</span>
          <span className="fig-nav-tagline">Personalized Career<br />Navigation</span>
        </div>
        <div className="fig-nav-right">
          <button className="nav-link" onClick={() => setShowCreatorsNote(true)}>
            Our Story
          </button>
          <button className="nav-download-btn" onClick={handleGetStarted}>
            Get Started
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-wordmark">
            <span>F</span><span>I</span><span>G</span>
          </h1>
          <h2 className="hero-tagline">helping you have an insane career</h2>
          <p className="hero-subtitle">
            AI-powered career exploration built around who you actually are
          </p>
          <button className="hero-cta" onClick={handleGetStarted}>
            let's do it
          </button>
        </div>
      </section>

      {/* Computer Panel */}
      <section className="computer-panel">
        <div className="computer-video-wrap">
          <video autoPlay muted loop playsInline
            style={{ width: '100%', display: 'block', mixBlendMode: 'screen' }}>
            <source src="/figcomputer.mov" type="video/mp4" />
            <source src="/figcomputer.mov" type="video/quicktime" />
          </video>
        </div>
      </section>

      {/* Press Bar */}
      <section style={{ background: '#000000', padding: '52px 32px 56px' }}>
        <p style={{
          fontFamily: "'Playfair Display', Georgia, serif",
          fontSize: 28,
          fontWeight: 700,
          color: 'rgba(255,255,255,0.85)',
          textAlign: 'center',
          marginBottom: 36,
        }}>
          team from
        </p>
        <div className="press-logos" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: '0',
          rowGap: 20,
        }}>
          {[
            { name: 'Columbia University',     style: { fontFamily: "'Cinzel', serif",        fontWeight: 600, fontSize: 20, letterSpacing: '0.06em' } },
            { name: 'University of Cambridge', style: { fontFamily: "'EB Garamond', serif",   fontWeight: 500, fontSize: 22, letterSpacing: '0.02em', fontStyle: 'italic' } },
            { name: 'Pinterest',               style: { fontFamily: "'Abril Fatface', serif", fontWeight: 400, fontSize: 24, letterSpacing: '0.01em' } },
            { name: 'Scale AI',                style: { fontFamily: "'Inter', sans-serif",    fontWeight: 600, fontSize: 19, letterSpacing: '-0.02em' } },
            { name: 'U.S. Federal Government', style: { fontFamily: "'Merriweather', serif",  fontWeight: 700, fontSize: 16, letterSpacing: '0.01em' } },
          ].map((school, i, arr) => (
            <React.Fragment key={school.name}>
              <span style={{
                color: '#ffffff',
                whiteSpace: 'nowrap',
                padding: '0 36px',
                ...school.style,
              }}>
                {school.name}
              </span>
              {i < arr.length - 1 && (
                <span className="press-divider" style={{ color: 'rgba(255,255,255,0.2)', fontSize: 28, userSelect: 'none' }}>|</span>
              )}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* Social Proof / Testimonials */}
      <section className="social-section">
        <div className="social-panel" ref={panelRef}>
          {/* animated fake cursor */}
          <div className="fake-cursor" ref={cursorRef}>
            <svg width="22" height="26" viewBox="0 0 22 26" fill="none">
              <path d="M1 1L1 21L6 16L9 24L12.5 22.5L9.5 14.5L16 14.5Z" fill="white" stroke="#111" strokeWidth="1.8" strokeLinejoin="round"/>
            </svg>
          </div>

          {/* top-left — small, hugging top-left */}
          <div ref={el => bubbleRefs.current[0] = el} className={`t-bubble t-bubble-left${visibleBubbles[0] ? ' pop-in' : ''}`}
            style={isMobile ? { top: '13%', left: '3%' } : { top: '6%', left: '5%' }}>
            <p className="t-bubble-quote">{testimonials[0].quote}</p>
            <p className="t-bubble-author">{testimonials[0].author} · {testimonials[0].role}</p>
          </div>

          <div ref={el => bubbleRefs.current[1] = el} className={`t-bubble t-bubble-right${visibleBubbles[1] ? ' pop-in' : ''}`}
            style={isMobile ? { top: '13%', right: '3%' } : { top: '5%', right: '5%' }}>
            <p className="t-bubble-quote">{testimonials[2].quote}</p>
            <p className="t-bubble-author">{testimonials[2].author} · {testimonials[2].role}</p>
          </div>

          <div ref={el => bubbleRefs.current[2] = el} className={`t-bubble t-bubble-left${visibleBubbles[2] ? ' pop-in' : ''}`}
            style={isMobile ? { top: '64%', left: '2%' } : { top: '58%', left: '2%' }}>
            <p className="t-bubble-quote">{testimonials[1].quote}</p>
            <p className="t-bubble-author">{testimonials[1].author} · {testimonials[1].role}</p>
          </div>

          <div ref={el => bubbleRefs.current[3] = el} className={`t-bubble t-bubble-right${visibleBubbles[3] ? ' pop-in' : ''}`}
            style={isMobile ? { top: '77%', right: '2%' } : { top: '64%', right: '5%' }}>
            <p className="t-bubble-quote">{testimonials[3].quote}</p>
            <p className="t-bubble-author">{testimonials[3].author} · {testimonials[3].role}</p>
          </div>

          <div ref={el => bubbleRefs.current[4] = el} className={`t-bubble t-bubble-left${visibleBubbles[4] ? ' pop-in' : ''}`}
            style={isMobile ? { top: '88%', left: '12%' } : { top: '80%', left: '28%' }}>
            <p className="t-bubble-quote">{testimonials[4].quote}</p>
            <p className="t-bubble-author">{testimonials[4].author} · {testimonials[4].role}</p>
          </div>

          {/* Centered headline */}
          <div className="social-content">
            <p className="social-eyebrow">What Fig users say</p>

            {/* Mobile-only horizontal scroll strip — sits between eyebrow and headline */}
            <div className="mobile-testimonials">
              {testimonials.map((t, i) => (
                <div key={i} className="mobile-t-card">
                  <p>"{t.quote}"</p>
                  <span>{t.author} · {t.role}</span>
                </div>
              ))}
            </div>

            <h2 className="social-headline">
              generated over 30K unique roadmaps<br />for people across 147 countries
            </h2>
            <button className="social-cta" onClick={handleGetStarted}>
              get started
            </button>
          </div>

        </div>
      </section>

      {/* Footer */}
      <footer className="fig-footer">
        <div className="footer-inner">
          <div className="footer-wordmark">FIG</div>
          <div className="footer-bottom">
            <div>
              <span className="footer-copy">© 2026 Fig Careers</span>
              <div className="footer-social">
                <a href="https://x.com/figcareer" target="_blank" rel="noopener noreferrer" className="footer-social-icon" aria-label="X">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
                <a href="https://www.tiktok.com/@figcareers" target="_blank" rel="noopener noreferrer" className="footer-social-icon" aria-label="TikTok">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.27 8.27 0 004.84 1.54V6.79a4.85 4.85 0 01-1.07-.1z" />
                  </svg>
                </a>
                <a href="https://www.instagram.com/getfigapp/" target="_blank" rel="noopener noreferrer" className="footer-social-icon" aria-label="Instagram">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </a>
              </div>
            </div>
            <div className="footer-links">
              <a href="/privacy" className="footer-link">Privacy Policy</a>
              <button
                className="footer-link"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                onClick={() => setShowCreatorsNote(true)}
              >
                Our Story
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Creators Note Modal */}
      {showCreatorsNote && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24,
          }}
          onClick={() => setShowCreatorsNote(false)}
        >
          <div style={{ position: 'absolute', inset: 0, backdropFilter: 'blur(6px)', background: 'rgba(0,0,0,0.5)' }} />
          <div
            className="modal-card"
            style={{
              position: 'relative',
              background: 'rgba(255,255,255,0.93)',
              backdropFilter: 'blur(24px)',
              borderRadius: 32,
              maxWidth: 580,
              width: '100%',
              maxHeight: '80vh',
              overflowY: 'auto',
              padding: '48px 52px',
              boxShadow: '0 24px 80px rgba(0,0,0,0.4)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowCreatorsNote(false)}
              style={{
                position: 'absolute', top: 20, right: 20,
                width: 32, height: 32, borderRadius: '50%',
                background: 'rgba(0,0,0,0.07)', border: 'none',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <X size={16} color="#1a1a1a" />
            </button>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, color: '#1a1a1a', fontFamily: "'Montserrat', sans-serif" }}>
              <h2 style={{ fontSize: 32, fontWeight: 900, marginBottom: 8 }}>notes from the creators</h2>
              <p style={{ fontSize: 15, lineHeight: 1.7 }}>
                we built Fig because we wanted to bring the fun back to figuring out what to do with your life.
              </p>
              <p style={{ fontSize: 15, lineHeight: 1.7 }}>
                <strong>why Fig?</strong> you might have heard of Sylvia Plath's fig tree from her book The Bell Jar. every branch a possible future, each one tempting, and the fear that choosing one means losing all the others.
              </p>
              <p style={{ fontSize: 15, lineHeight: 1.7 }}>
                we've built this discovery agent so that you never let your figs fall.
              </p>
              <p style={{ fontSize: 15, lineHeight: 1.7 }}>
                to help you move forward by surfacing opportunities you didn't know were open to you
              </p>
              <p style={{ fontSize: 15, lineHeight: 1.7 }}>
                we hope you have as much fun trying Fig as we had building it (we put a lot of love and sweat into it haha)
              </p>
              <p style={{ fontSize: 15, lineHeight: 1.7 }}>
                because we believe that life's already hard, so why not find ways to make it a lil more exciting!
              </p>
              <p style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.7 }}>happy exploring.</p>
              <p style={{ fontSize: 20, fontWeight: 900 }}>-Fig Team</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
